<template>
  <div class="app">
    <!-- 动态背景 -->
    <div class="background-effects">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
      <div class="snowflakes">
        <div class="snowflake" v-for="i in 30" :key="i" :style="getSnowflakeStyle(i)">❅</div>
      </div>
    </div>

    <div v-if="isloading" class="fullscreen-loader">
      <fulfilling-bouncing-circle-spinner
        :animation-duration="1000"
        :size="60"
        color="#ff1d5e"
      />
    </div>

    <main class="main-content" v-show="!isloading">
      <div class="logo-container">
        <img class="logo" src="/logo.png" alt="">
        <div class="logo-glow"></div>
      </div>
      
      <h1 class="title">
        <span class="gradient-text">Squash Pack</span>
      </h1>
      
      <p class="subtitle">
        <span class="highlight">批量处理</span> PNG 图片压缩并合并成 <span class="highlight">WebP</span>
      </p>

      <div class="download-buttons">
        <a :href="windowsDownloadLink" class="download-button">
          <div class="button-background"></div>
          <div class="button-content">
            <DownloadIcon class="icon" />
            <span>下载 Windows 版本</span>
          </div>
          <div class="button-shimmer"></div>
          <div class="button-glow"></div>
        </a>
        <a :href="macosx86DownloadLink" class="download-button">
          <div class="button-background"></div>
          <div class="button-content">
            <DownloadIcon class="icon" />
            <span>下载 macOS Intel芯片 版本</span>
          </div>
          <div class="button-shimmer"></div>
          <div class="button-glow"></div>
        </a>
        <a :href="macosaarchDownloadLink" class="download-button">
          <div class="button-background"></div>
          <div class="button-content">
            <DownloadIcon class="icon" />
            <span>下载 macOS M芯片 版本</span>
          </div>
          <div class="button-shimmer"></div>
          <div class="button-glow"></div>
        </a>
      </div>

      <div class="features">
        <h2>
          <span class="features-icon">✨</span>
          主要功能
        </h2>
        <ul>
          <li>
            <span class="feature-icon">🎨</span>
            <span>批量处理 PNG 图片压缩</span>
          </li>
          <li>
            <span class="feature-icon">🔗</span>
            <span>将压缩后的图片合并成 WebP 格式</span>
          </li>
          <li>
            <span class="feature-icon">⏱️</span>
            <span>支持配置帧时间</span>
          </li>
          <li>
            <span class="feature-icon">🔄</span>
            <span>可调整循环次数</span>
          </li>
          <li>
            <span class="feature-icon">⚙️</span>
            <span>自定义输出细节</span>
          </li>
        </ul>
      </div>
    </main>

    <footer>
      <p>Squash Pack &copy; {{ currentYear }} By Doone. 保留所有权利。</p>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { DownloadIcon } from 'lucide-vue-next'
import { FulfillingBouncingCircleSpinner } from 'epic-spinners'
import axios from 'axios';

const windowsDownloadLink = ref('https://squash.internal.52yuwan.com/download/Squash%20Pack_win64.exe')
const macosaarchDownloadLink = ref('https://squash.internal.52yuwan.com/download/Squash%20Pack_aarch64.dmg')
const macosx86DownloadLink = ref('https://squash.internal.52yuwan.com/download/Squash%20Pack_x86.dmg')

const currentYear = computed(() => new Date().getFullYear())
const isloading = ref(true);

const getSnowflakeStyle = () => {
  const randomX = Math.random() * 100;
  const randomDelay = Math.random() * 10;
  const randomDuration = 10 + Math.random() * 20;
  const randomSize = 0.5 + Math.random() * 1.5;
  const randomOpacity = 0.3 + Math.random() * 0.7;
  
  return {
    left: `${randomX}%`,
    fontSize: `${randomSize}rem`,
    opacity: randomOpacity,
    animationDelay: `${randomDelay}s`,
    animationDuration: `${randomDuration}s`
  };
};

onMounted(async () => {
  try {
    const response = await axios.get('https://api.internal.52yuwan.com/squash/release');
    console.log(response.data);
    const {code, mac_aarch, mac_x64, win} = response.data;
    if (code === 200) {
      windowsDownloadLink.value = win;
      macosaarchDownloadLink.value = mac_aarch;
      macosx86DownloadLink.value = mac_x64;
      isloading.value = false;
    }
  } catch (error) {
    console.error('获取release失败:', error);
  }
});
</script>

<style lang="less" scoped>
@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
}

@keyframes orb-float-1 {
  0%, 100% { 
    transform: translate(0, 0) scale(1);
  }
  50% { 
    transform: translate(50px, -50px) scale(1.1);
  }
}

@keyframes orb-float-2 {
  0%, 100% { 
    transform: translate(0, 0) scale(1);
  }
  50% { 
    transform: translate(-50px, 50px) scale(0.9);
  }
}

@keyframes orb-float-3 {
  0%, 100% { 
    transform: translate(0, 0) scale(1);
  }
  50% { 
    transform: translate(30px, 60px) scale(1.05);
  }
}

@keyframes orb-breathe {
  0%, 100% {
    opacity: 0.3;
    filter: blur(60px) brightness(1);
  }
  50% {
    opacity: 0.6;
    filter: blur(80px) brightness(1.3);
  }
}

@keyframes shimmer {
  0% { 
    transform: translateX(-100%) skewX(-15deg); 
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% { 
    transform: translateX(100%) skewX(-15deg); 
    opacity: 0;
  }
}

@keyframes button-glow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(0.95);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.9);
    opacity: 1;
  }
  100% {
    transform: scale(1.1);
    opacity: 0;
  }
}

@keyframes glow-pulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.1); }
}

@keyframes gradient-shift {
  0%, 100% { 
    background-position: 0% 50%;
  }
  50% { 
    background-position: 100% 50%;
  }
}

@keyframes snowfall {
  0% {
    transform: translateY(-10vh) translateX(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(110vh) translateX(100px);
    opacity: 0;
  }
}

.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: #f7fafc;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  position: relative;
  overflow-x: hidden;

  .background-effects {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0;
    pointer-events: none;

    .snowflakes {
      position: absolute;
      width: 100%;
      height: 100%;
      
      .snowflake {
        position: absolute;
        top: -10vh;
        color: rgba(255, 255, 255, 0.8);
        text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
        animation: snowfall linear infinite;
        user-select: none;
        pointer-events: none;
      }
    }

    .gradient-orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(60px);
      animation: 
        orb-breathe 4s ease-in-out infinite,
        orb-float-1 20s ease-in-out infinite;
    }

    .orb-1 {
      width: 500px;
      height: 500px;
      background: radial-gradient(circle, #ff1d5e 0%, #ff6b9d 30%, transparent 70%);
      top: 10%;
      left: 10%;
      animation: 
        orb-breathe 5s ease-in-out infinite,
        orb-float-1 25s ease-in-out infinite;
    }

    .orb-2 {
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, #3182ce 0%, #60a5fa 30%, transparent 70%);
      bottom: 10%;
      right: 10%;
      animation: 
        orb-breathe 6s ease-in-out infinite 1s,
        orb-float-2 30s ease-in-out infinite;
    }

    .orb-3 {
      width: 350px;
      height: 350px;
      background: radial-gradient(circle, #9333ea 0%, #c084fc 30%, transparent 70%);
      top: 50%;
      left: 50%;
      animation: 
        orb-breathe 7s ease-in-out infinite 2s,
        orb-float-3 35s ease-in-out infinite;
    }
  }

  .main-content {
    position: relative;
    z-index: 1;
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    box-sizing: border-box;

    .logo-container {
      position: relative;
      margin-bottom: -1rem;
      animation: float 6s ease-in-out infinite;

      .logo {
        position: relative;
        width: 230px;
        height: 230px;
        filter: drop-shadow(0 0 30px rgba(255, 29, 94, 0.5));
        z-index: 2;
      }

      .logo-glow {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 220px;
        height: 220px;
        background: radial-gradient(circle, rgba(255, 29, 94, 0.3) 0%, transparent 70%);
        border-radius: 50%;
        animation: glow-pulse 3s ease-in-out infinite;
        z-index: 1;
      }
    }

    .title {
      margin-bottom: 0.8rem;
      
      .gradient-text {
        font-size: 2.5rem;
        font-weight: 900;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #00f2fe 100%);
        background-size: 200% 200%;
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        animation: gradient-shift 5s ease infinite;
        text-align: center;
        display: inline-block;
        letter-spacing: -1px;
      }
    }

    .subtitle {
      font-size: 1.1rem;
      color: #cbd5e0;
      margin-bottom: 2rem;
      text-align: center;
      line-height: 1.6;

      .highlight {
        color: #f093fb;
        font-weight: 600;
        text-shadow: 0 0 10px rgba(240, 147, 251, 0.5);
      }
    }

    .download-buttons {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2.5rem;
      width: 100%;
      max-width: 40rem;

      .download-button {
        position: relative;
        overflow: hidden;
        padding: 1.2rem 1.5rem;
        background: transparent;
        border: 2px solid rgba(255, 255, 255, 0.1);
        color: white;
        font-size: 1rem;
        font-weight: 600;
        border-radius: 1rem;
        text-decoration: none;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        box-shadow: 
          0 4px 20px rgba(0, 0, 0, 0.3),
          inset 0 1px 0 rgba(255, 255, 255, 0.1);

        .button-background {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(99, 102, 241, 0.8) 0%, 
            rgba(168, 85, 247, 0.8) 50%,
            rgba(236, 72, 153, 0.8) 100%);
          opacity: 0.9;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }

        .button-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: 2;
          pointer-events: none;
        }

        .button-content {
          position: relative;
          z-index: 3;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          transition: transform 0.3s ease;
        }

        .button-shimmer {
          position: absolute;
          top: 0;
          left: 0;
          width: 50%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.3),
            transparent
          );
          transform: translateX(-100%) skewX(-15deg);
          z-index: 4;
          pointer-events: none;
        }

        &:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 
            0 15px 50px rgba(168, 85, 247, 0.5),
            0 5px 20px rgba(236, 72, 153, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.3);

          .button-background {
            opacity: 1;
            transform: scale(1.05);
          }

          .button-glow {
            opacity: 1;
            animation: button-glow 2s ease-in-out infinite;
          }

          .button-shimmer {
            animation: shimmer 1.2s ease-in-out infinite;
          }

          .button-content {
            transform: scale(1.05);
          }
        }

        &:active {
          transform: translateY(-4px) scale(0.98);
        }

        .icon {
          height: 1.5rem;
          width: 1.5rem;
          filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.5));
          transition: all 0.3s ease;
        }

        &:hover .icon {
          transform: rotate(360deg) scale(1.1);
        }
      }
    }

    .features {
      background: rgba(45, 55, 72, 0.6);
      backdrop-filter: blur(10px);
      padding: 1.5rem;
      border-radius: 1.2rem;
      max-width: 40rem;
      width: 100%;
      border: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

      h2 {
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 1.2rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .features-icon {
          -webkit-text-fill-color: initial;
          font-size: 1.5rem;
        }
      }

      ul {
        list-style: none;
        padding: 0;
        color: #e2e8f0;

        li {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          margin-bottom: 0.8rem;
          padding: 0.6rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 0.6rem;
          transition: all 0.3s ease;
          font-size: 0.95rem;

          &:hover {
            background: rgba(255, 255, 255, 0.1);
            transform: translateX(10px);
          }

          .feature-icon {
            font-size: 1.3rem;
            flex-shrink: 0;
          }

          span:last-child {
            flex: 1;
          }
        }
      }
    }
  }

  footer {
    position: relative;
    z-index: 1;
    background: rgba(45, 55, 72, 0.8);
    backdrop-filter: blur(10px);
    padding: 1.5rem;
    text-align: center;
    color: #a0aec0;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
}

.fullscreen-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(15, 12, 41, 0.95) 0%, rgba(48, 43, 99, 0.95) 50%, rgba(36, 36, 62, 0.95) 100%);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>